import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Search,
  Check,
  Clock,
  Sparkles,
  RefreshCw,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { serviceRequestAPI } from '../../services/api';
import { useInteractionMode } from '../../hooks/useInteractionMode';
import { toast } from 'sonner';
import ServiceFormModal from '../../components/services/hub/ServiceFormModal';
import ProfileAssessmentModal from '../../components/services/hub/ProfileAssessmentModal';
import profileAssessmentIcon from '../../assets/Icon/Profile Assessment.png';
import preapplicationSupportIcon from '../../assets/Icon/Preapplication Support.png';
import applyUniversityIcon from '../../assets/Icon/Apply University.png';
import visaInterviewIcon from '../../assets/Icon/Visa & Interview Support.png';
import ticketTravelIcon from '../../assets/Icon/Ticket & Travel Support.png';
import accommodationIcon from '../../assets/Icon/Find Accommodation.png';
import educationLoanIcon from '../../assets/Icon/Education Loan.png';
import jobsAbroadIcon from '../../assets/Icon/Find Jobs Abroad.png';
import serviceImage1 from '../../assets/services-img/services-1.png';
import serviceImage2 from '../../assets/services-img/services-2.png';
import serviceImage3 from '../../assets/services-img/services-3.png';
import serviceImage4 from '../../assets/services-img/services-4.png';
import serviceImage5 from '../../assets/services-img/services-5.png';
import serviceImage6 from '../../assets/services-img/services-6.png';
import serviceImage7 from '../../assets/services-img/services-7.jpg';
import serviceImage8 from '../../assets/services-img/services-8.png';

// 8 Services data matching backend SERVICE_TYPES
const SERVICES_DATA = [
  {
    id: 'PROFILE_ASSESSMENT',
    slug: 'profile-assessment',
    name: 'Profile Assessment',
    description:
      'Comprehensive evaluation of your academic and professional profile to identify the best opportunities.',
    duration: '2-3 days',
    startingAt: '$99',
    color: 'bg-blue-500',
    icon: '🎯',
    features: [
      'Personalized evaluation',
      'University recommendations',
      'Gap analysis',
    ],
    popular: true,
  },
  {
    id: 'UNIVERSITY_SHORTLISTING',
    slug: 'preapplication-support',
    name: 'Preapplication Support',
    description:
      'Expert guidance to identify and shortlist universities that match your profile and goals.',
    duration: '1-2 weeks',
    startingAt: '$199',
    color: 'bg-purple-500',
    icon: '🎓',
    features: ['Profile matching', 'Budget analysis', 'Safety/Reach schools'],
    popular: true,
  },
  {
    id: 'APPLICATION_ASSISTANCE',
    slug: 'apply-university',
    name: 'Apply University',
    description:
      'End-to-end university application assistance including SOP, LOR, and submission.',
    duration: '2-4 weeks',
    startingAt: '$499',
    color: 'bg-pink-500',
    icon: '📄',
    features: ['SOP drafting', 'Application filling', 'Document review'],
    popular: true,
  },
  {
    id: 'VISA_GUIDANCE',
    slug: 'visa-interview',
    name: 'Visa & Interview Support',
    description:
      'Complete visa application guidance and mock interview preparation.',
    duration: '1-3 weeks',
    startingAt: '$349',
    color: 'bg-orange-500',
    icon: '✈️',
    features: ['Visa documentation', 'Form filling', 'Mock interviews'],
  },
  {
    id: 'SCHOLARSHIP_SEARCH',
    slug: 'ticket-travel',
    name: 'Ticket & Travel Support',
    description:
      'Find scholarships you qualify for and assistance with applications.',
    duration: '1-2 weeks',
    startingAt: '$149',
    color: 'bg-amber-500',
    icon: '🏆',
    features: ['Scholarship matching', 'Essay review', 'Application support'],
  },
  {
    id: 'LOAN_ASSISTANCE',
    slug: 'education-loan',
    name: 'Education Loan',
    description: 'Financial assistance and loan processing with partner banks.',
    duration: '2-4 weeks',
    startingAt: '$199',
    color: 'bg-rose-500',
    icon: '💰',
    features: ['Loan comparison', 'Documentation', 'Application support'],
  },
  {
    id: 'ACCOMMODATION_HELP',
    slug: 'accommodation',
    name: 'Find Accommodation',
    description:
      'Housing search and setup assistance in your destination country.',
    duration: '1-2 weeks',
    startingAt: '$199',
    color: 'bg-green-500',
    icon: '🏠',
    features: ['Housing search', 'Lease review', 'Virtual tours'],
  },
  {
    id: 'PRE_DEPARTURE_ORIENTATION',
    slug: 'jobs-abroad',
    name: 'Find Jobs Abroad',
    description:
      'Comprehensive preparation for your journey abroad including culture and logistics.',
    duration: '3-5 days',
    startingAt: '$99',
    color: 'bg-teal-500',
    icon: '💼',
    features: ['Culture prep', 'Packing guide', 'Banking setup'],
  },
];

const SERVICE_ASSETS = {
  PROFILE_ASSESSMENT: {
    iconAsset: profileAssessmentIcon,
    imageAsset: serviceImage1,
  },
  UNIVERSITY_SHORTLISTING: {
    iconAsset: preapplicationSupportIcon,
    imageAsset: serviceImage2,
  },
  APPLICATION_ASSISTANCE: {
    iconAsset: applyUniversityIcon,
    imageAsset: serviceImage3,
  },
  VISA_GUIDANCE: {
    iconAsset: visaInterviewIcon,
    imageAsset: serviceImage4,
  },
  SCHOLARSHIP_SEARCH: {
    iconAsset: ticketTravelIcon,
    imageAsset: serviceImage5,
  },
  ACCOMMODATION_HELP: {
    iconAsset: accommodationIcon,
    imageAsset: serviceImage6,
  },
  LOAN_ASSISTANCE: {
    iconAsset: educationLoanIcon,
    imageAsset: serviceImage7,
  },
  PRE_DEPARTURE_ORIENTATION: {
    iconAsset: jobsAbroadIcon,
    imageAsset: serviceImage8,
  },
};

const SERVICE_VISUALS = {
  PROFILE_ASSESSMENT: {
    shell: 'from-[#efe3ff] via-[#e3d2ff] to-[#d2b7ff]',
    glow: 'shadow-[0_10px_30px_rgba(168,125,250,0.25)]',
    border: 'border-[#9b6ffc]/55',
    tint: 'from-[#a87dfa]/48 via-[#a87dfa]/18 to-transparent',
    title: 'text-[#6d49d4]',
    chip: 'text-[#7a55df]',
    iconWrap: 'border-[#a87dfa]/40 bg-[#f3e9ff]',
    button: 'border-[#a87dfa]/45 bg-[#f7efff] text-[#6d49d4] hover:bg-[#efe0ff]',
  },
  UNIVERSITY_SHORTLISTING: {
    shell: 'from-[#e4f0ff] via-[#d2e6ff] to-[#bddbff]',
    glow: 'shadow-[0_10px_30px_rgba(111,168,255,0.24)]',
    border: 'border-[#6fa8ff]/55',
    tint: 'from-[#6fa8ff]/48 via-[#6fa8ff]/18 to-transparent',
    title: 'text-[#3f7edc]',
    chip: 'text-[#4a8cf1]',
    iconWrap: 'border-[#6fa8ff]/40 bg-[#edf5ff]',
    button: 'border-[#6fa8ff]/45 bg-[#edf5ff] text-[#3f7edc] hover:bg-[#dfedff]',
  },
  APPLICATION_ASSISTANCE: {
    shell: 'from-[#ffe3ee] via-[#ffd2e5] to-[#ffc0da]',
    glow: 'shadow-[0_10px_30px_rgba(255,155,194,0.24)]',
    border: 'border-[#ff9bc2]/55',
    tint: 'from-[#ff9bc2]/46 via-[#ff9bc2]/18 to-transparent',
    title: 'text-[#d45f93]',
    chip: 'text-[#dd6f9f]',
    iconWrap: 'border-[#ff9bc2]/40 bg-[#fff0f7]',
    button: 'border-[#ff9bc2]/45 bg-[#fff0f7] text-[#d45f93] hover:bg-[#ffe3f0]',
  },
  VISA_GUIDANCE: {
    shell: 'from-[#dffaf5] via-[#cdf4eb] to-[#b4eadf]',
    glow: 'shadow-[0_10px_30px_rgba(120,214,198,0.24)]',
    border: 'border-[#78d6c6]/55',
    tint: 'from-[#78d6c6]/46 via-[#78d6c6]/18 to-transparent',
    title: 'text-[#24998a]',
    chip: 'text-[#2aa898]',
    iconWrap: 'border-[#78d6c6]/40 bg-[#ecfffb]',
    button: 'border-[#78d6c6]/45 bg-[#ecfffb] text-[#24998a] hover:bg-[#dcfaf3]',
  },
  SCHOLARSHIP_SEARCH: {
    shell: 'from-[#fff0d8] via-[#ffe5be] to-[#ffd497]',
    glow: 'shadow-[0_10px_30px_rgba(255,191,102,0.24)]',
    border: 'border-[#ffbf66]/55',
    tint: 'from-[#ffbf66]/46 via-[#ffbf66]/18 to-transparent',
    title: 'text-[#d28a20]',
    chip: 'text-[#e59a28]',
    iconWrap: 'border-[#ffbf66]/40 bg-[#fff7ea]',
    button: 'border-[#ffbf66]/45 bg-[#fff7ea] text-[#d28a20] hover:bg-[#ffeed4]',
  },
  ACCOMMODATION_HELP: {
    shell: 'from-[#e2f7dc] via-[#d1efc8] to-[#b8e4ab]',
    glow: 'shadow-[0_10px_30px_rgba(143,209,127,0.24)]',
    border: 'border-[#8fd17f]/55',
    tint: 'from-[#8fd17f]/46 via-[#8fd17f]/18 to-transparent',
    title: 'text-[#4e9b40]',
    chip: 'text-[#5aa64a]',
    iconWrap: 'border-[#8fd17f]/40 bg-[#f1ffec]',
    button: 'border-[#8fd17f]/45 bg-[#f1ffec] text-[#4e9b40] hover:bg-[#e3f8db]',
  },
  LOAN_ASSISTANCE: {
    shell: 'from-[#dff3ff] via-[#caeaff] to-[#addbff]',
    glow: 'shadow-[0_10px_30px_rgba(126,200,255,0.24)]',
    border: 'border-[#7ec8ff]/55',
    tint: 'from-[#7ec8ff]/46 via-[#57a8ff]/18 to-transparent',
    title: 'text-[#3b84c7]',
    chip: 'text-[#4794db]',
    iconWrap: 'border-[#7ec8ff]/40 bg-[#edf8ff]',
    button: 'border-[#7ec8ff]/45 bg-[#edf8ff] text-[#3b84c7] hover:bg-[#dff1ff]',
  },
  PRE_DEPARTURE_ORIENTATION: {
    shell: 'from-[#ffe4dd] via-[#ffd3c8] to-[#ffbdac]',
    glow: 'shadow-[0_10px_30px_rgba(255,159,139,0.24)]',
    border: 'border-[#ff9f8b]/55',
    tint: 'from-[#ff9f8b]/46 via-[#ff9f8b]/18 to-transparent',
    title: 'text-[#d36852]',
    chip: 'text-[#e17a65]',
    iconWrap: 'border-[#ff9f8b]/40 bg-[#fff1ed]',
    button: 'border-[#ff9f8b]/45 bg-[#fff1ed] text-[#d36852] hover:bg-[#ffe2d9]',
  },
};

export default function ExploreServices() {
  const { isRepManaged, canApplyServices } = useInteractionMode();
  const [services] = useState(SERVICES_DATA);
  const [appliedServices, setAppliedServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceFormModal, setShowServiceFormModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);

  useEffect(() => {
    fetchAppliedServices();
  }, []);

  const fetchAppliedServices = async () => {
    try {
      const response = await serviceRequestAPI.getAll();
      const applications = response.serviceRequests || response.data || [];
      // Get list of applied service types
      const appliedTypes = applications
        .filter(app => app.status !== 'CANCELLED')
        .map(app => app.serviceType);
      setAppliedServices(appliedTypes);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load your applications');
    } finally {
      setLoading(false);
    }
  };

  const executeApply = async (formData, representativeName) => {
    if (!selectedService) return;

    setApplying(selectedService.id);
    try {
      await serviceRequestAPI.create(
        selectedService.id,
        {},
        formData || null,
        representativeName,
      );
      toast.success('Service application submitted successfully!');
      setAppliedServices(prev =>
        prev.includes(selectedService.id) ? prev : [...prev, selectedService.id]
      );
      setShowAssessmentModal(false);
      setShowServiceFormModal(false);
      setSelectedService(null);
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to apply for service',
      );
      throw error;
    } finally {
      setApplying(null);
    }
  };

  const handleApply = service => {
    if (isRepManaged) {
      toast.error('Your account is managed by a representative. They will apply for services on your behalf.');
      return;
    }
    setSelectedService(service);
    if (service.slug === 'profile-assessment') {
      setShowAssessmentModal(true);
      return;
    }
    setShowServiceFormModal(true);
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFilter =
      filter === 'all'
        ? true
        : filter === 'popular'
          ? service.popular
          : filter === 'applied'
            ? appliedServices.includes(service.id)
            : true;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    applied: appliedServices.length,
    available: SERVICES_DATA.length - appliedServices.length,
    popular: SERVICES_DATA.filter(s => s.popular).length,
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="explore-services-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-outfit font-bold text-slate-900">
              Explore Services
            </h1>
            <p className="text-slate-500 mt-1">
              Discover and apply for services to support your journey
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchAppliedServices}
            className="rounded-xl border-slate-200 hover:border-sky-300 hover:text-sky-700 transition-all"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card className="p-5 bg-gradient-to-br from-sky-100 to-sky-50/80 border border-sky-200/80 rounded-2xl hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-sky-500/20">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.applied}
                </p>
                <p className="text-sm text-slate-500 font-medium">
                  Services Applied
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-emerald-100 to-emerald-50/80 border border-emerald-200/80 rounded-2xl hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.available}
                </p>
                <p className="text-sm text-slate-500 font-medium">
                  Available to Apply
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-violet-100 to-violet-50/80 border border-violet-200/80 rounded-2xl hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.popular}
                </p>
                <p className="text-sm text-slate-500 font-medium">
                  Popular Services
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-64 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl border-slate-200 bg-white shadow-sm focus:shadow-md focus:border-sky-300 transition-all"
              data-testid="search-services-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={`rounded-xl ${filter === 'all' ? 'bg-sky-600 hover:bg-sky-700 shadow-md shadow-sky-500/25' : 'border-slate-200 hover:border-sky-200'}`}
              data-testid="filter-all"
            >
              All Services
            </Button>
            <Button
              variant={filter === 'popular' ? 'default' : 'outline'}
              onClick={() => setFilter('popular')}
              className={`rounded-xl ${filter === 'popular' ? 'bg-sky-600 hover:bg-sky-700 shadow-md shadow-sky-500/25' : 'border-slate-200 hover:border-sky-200'}`}
              data-testid="filter-popular"
            >
              Popular
            </Button>
            <Button
              variant={filter === 'applied' ? 'default' : 'outline'}
              onClick={() => setFilter('applied')}
              className={`rounded-xl ${filter === 'applied' ? 'bg-sky-600 hover:bg-sky-700 shadow-md shadow-sky-500/25' : 'border-slate-200 hover:border-sky-200'}`}
              data-testid="filter-applied"
            >
              Applied
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map(service => {
            const isApplied = appliedServices.includes(service.id);
            const isApplying = applying === service.id;
            const visuals = SERVICE_VISUALS[service.id] || {};
            const assets = SERVICE_ASSETS[service.id] || {};

            return (
              <div
                key={service.id}
                className={`partner-card partner-card-hover relative flex h-full flex-col rounded-[28px] border bg-gradient-to-br p-4 ${visuals.shell || 'from-white/70 to-white/40'} ${visuals.border || 'border-[#A87DFA]/24'} ${visuals.glow || ''}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className={`absolute inset-x-0 top-0 h-28 rounded-t-[28px] bg-gradient-to-b ${visuals.tint || 'from-[#A87DFA]/20 via-[#A87DFA]/8 to-transparent'} pointer-events-none`} />
                <div className="relative mb-4 overflow-hidden rounded-[22px] border border-[#A87DFA]/18">
                  <img
                    src={assets.imageAsset}
                    alt={service.name}
                    className="h-40 w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.18))]" />
                  <div className="absolute left-4 top-4 flex h-14 w-14 items-center justify-center rounded-[18px] border border-white/25 bg-white/20 backdrop-blur-md">
                    <img src={assets.iconAsset} alt="" className="h-8 w-8 object-contain" />
                  </div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className={`rounded-full border border-white/35 bg-white/55 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] backdrop-blur-md ${visuals.chip || 'text-[#7A55DF]'}`}>
                      Fly8 Service
                    </div>
                    {service.popular && (
                      <span className="rounded-full border border-white/35 bg-white/55 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 backdrop-blur-md">
                        Popular
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[#8D68E8]">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-2xl border ${visuals.iconWrap || 'border-[#A87DFA]/16 bg-[linear-gradient(135deg,rgba(168,125,250,0.16),rgba(168,125,250,0.05))]'}`}>
                    <img src={assets.iconAsset} alt="" className="h-4 w-4 object-contain" />
                  </div>
                  <span className={visuals.chip || 'text-[#7A55DF]'}>Student Service</span>
                </div>

                <div className="relative z-10 flex flex-1 flex-col">
                  <h3 className={`mb-2 text-xl font-semibold ${visuals.title || 'text-[#6d49d4]'}`}>
                    {service.name}
                  </h3>

                  <p className="mb-4 text-sm leading-relaxed text-slate-600">
                    {service.description}
                  </p>

                  <div className="mb-5">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Features:
                    </p>

                    <div className="flex flex-wrap gap-1.5">
                      {service.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="rounded-lg border border-white/60 bg-white/50 px-2.5 py-1 text-xs font-medium text-slate-600 backdrop-blur-sm"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {isApplied ? (
                    <button
                      disabled
                      className="w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-white/80 py-2.5 px-4 font-semibold text-slate-500 opacity-70"
                      data-testid={`applied-${service.id}`}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <Check className="w-4 h-4" />
                        Already Applied
                      </span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApply(service)}
                      disabled={isApplying}
                      className={`group w-full rounded-2xl border bg-white/65 px-4 py-2.5 font-semibold transition-colors disabled:opacity-70 ${visuals.button || 'partner-secondary-btn'}`}
                      data-testid={`apply-${service.id}`}
                    >
                      {isApplying ? (
                        <span className="inline-flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Applying...
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center gap-1">
                          Apply Now
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredServices.length === 0 && (
          <Card className="p-12 text-center rounded-2xl border border-slate-100">
            <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Search className="w-7 h-7 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">
              No services found matching your criteria
            </p>
          </Card>
        )}

        <ProfileAssessmentModal
          open={showAssessmentModal}
          onOpenChange={(open) => {
            setShowAssessmentModal(open);
            if (!open) {
              setSelectedService(null);
            }
          }}
          onApplySuccess={executeApply}
        />

        <ServiceFormModal
          open={showServiceFormModal}
          onOpenChange={(open) => {
            setShowServiceFormModal(open);
            if (!open) {
              setSelectedService(null);
            }
          }}
          serviceSlug={selectedService?.slug}
          onSubmit={executeApply}
        />
      </div>
    </DashboardLayout>
  );
}
